import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  MapContainer,
  TileLayer,
  Popup,
  CircleMarker,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import RecenterButton from "../components/RecenterButton";
import L from "leaflet";
import DriverLayout from "../components/DriverLayout";
import ProfilePictureCropper from "../components/ProfilePictureCropper";
import UserDetailsOverlay from "../components/UserDetailsOverlay";
import ActiveBookingsPanel from "../components/ActiveBookingsPanel";
import HireAssistanceBubble from "../components/HireAssistanceBubble";
import { useDispatch, useSelector } from "react-redux";
import BookingAlertOverlay from "../components/BookingAlertOverlay";
import EmergencyAlertModal from "../components/EmergencyAlertModal";
import api from "../services/api";
import { updateEmergency } from "../services/vehicleService";
import { calculateDistance, estimateETA, calculateFare } from "../services/mapsService";
import signalrService from "../services/signalr";
import locationWS from "../services/locationWebSocket";
import bookingWS from "../services/bookingWebSocket";
import chatWS from "../services/chatWebSocket";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { uploadProfilePicture, getProfilePictureUrl } from "../services/userService";
import { 
  ChevronLeft, 
  ChevronRight, 
  AlertTriangle, 
  MapPin, 
  Truck, 
  User, 
  Phone, 
  Wifi, 
  WifiOff,
  Calendar,
  Clock,
  Navigation,
  CheckCircle,
  XCircle
} from "lucide-react";
import { defaultUserImg } from "../assets/defaultUser";
import { fetchBookings } from "../actions/BookingActions";
import { fetchUsers } from "../actions/UserActions";
import { fetchVehicles } from "../actions/VehicleActions";
import { getBookingDetailsByVehicleId, getForkBookingDetailsAll } from "../services/bookingService";

// Import vehicle icons
import carIcon from "../assets/car.png";
import vanIcon from "../assets/van.png";
import busIcon from "../assets/bus.png";
import lorryIcon from "../assets/lorry.png";
import suvIcon from "../assets/suv.png";
import cabIcon from "../assets/cab.png";
import forkliftIcon from "../assets/fork lift.png";
import dockyardIcon from "../assets/dockyard.png";
import cherryIcon from "../assets/cherry.png";
import craneIcon from "../assets/crane.jpg";

// Fix Leaflet icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const AnimatedMapCenter = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position && map && map._container) {
      try {
        map.flyTo(position, 16, { animate: true, duration: 1.5 });
      } catch (err) {
        console.warn("Map animation error:", err);
      }
    }
  }, [position, map]);
  return null;
};

// Stat Card Component
function StatCard({ title, value, icon: Icon, color }) {
  const colorClasses = {
    blue: "bg-blue-50 border-blue-200 text-blue-600",
    green: "bg-emerald-50 border-emerald-200 text-emerald-600",
    red: "bg-red-50 border-red-200 text-red-600",
    yellow: "bg-yellow-50 border-yellow-200 text-yellow-600",
  };
  
  const bgColor = colorClasses[color] || colorClasses.blue;
  
  return (
    <div className={`${bgColor} border rounded-lg p-3`}>
      <div className="flex items-center justify-between mb-1">
        <Icon size={14} className={bgColor.split(' ')[2]} />
        <span className="text-lg font-bold">{value}</span>
      </div>
      <p className="text-xs font-medium">{title}</p>
    </div>
  );
}

// Status Badge Component
function StatusBadge({ isOnline }) {
  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
      isOnline ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"
    }`}>
      {isOnline ? <Wifi size={10} /> : <WifiOff size={10} />}
      {isOnline ? "Online" : "Offline"}
    </div>
  );
}

// Vehicle Info Card Component
function VehicleInfoCard({ vehicle }) {
  const getVehicleIcon = (type) => {
    const typeMap = {
      Car: carIcon, Van: vanIcon, Bus: busIcon, Lorry: lorryIcon,
      SUV: suvIcon, Cab: cabIcon, Forklift: forkliftIcon,
      "Cherry-Picker": cherryIcon, Crane: craneIcon, Trailer: lorryIcon
    };
    return typeMap[type] || carIcon;
  };

  if (!vehicle.vehicleId) return null;

  return (
    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-3">
      <div className="flex items-center gap-3">
        <img src={getVehicleIcon(vehicle.vehicleType)} alt={vehicle.vehicleType} className="w-10 h-10 object-contain" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-indigo-900 truncate">{vehicle.vehicleId}</p>
          <p className="text-[10px] text-indigo-600">{vehicle.vehicleType || 'Unknown'}</p>
        </div>
        <StatusBadge isOnline={vehicle.oStatus === "O"} />
      </div>
    </div>
  );
}

// Weather Card Component
function WeatherCard({ weather }) {
  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-3">
      <div className="flex items-center gap-3">
        <span className="text-2xl">🌤️</span>
        <div className="flex-1">
          <h3 className="text-xs font-semibold text-amber-900">Weather Alert</h3>
          <p className="text-[11px] text-amber-800">{weather}</p>
        </div>
      </div>
    </div>
  );
}

export default function DriverDashboard() {
  const dispatch = useDispatch();
  const { authKey, userDetails } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  
  const [driver, setDriver] = useState({
    name: "",
    driverSno: "",
    vehicleId: "",
    vehicleType: "",
    mobileNo: "",
    profilePic: defaultUserImg,
    role: "D",
    status: "A",
    password: "",
    file_path: null,
  });

  const [currentVehicle, setCurrentVehicle] = useState(null);
  const [isOnline, setIsOnline] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [hasActiveBooking, setHasActiveBooking] = useState(false);
  const [vehicleLocation, setVehicleLocation] = useState({ latitude: 6.95417, longitude: 79.86611 });
  const [hasLocationFix, setHasLocationFix] = useState(false);
  const [incomingBooking, setIncomingBooking] = useState(null);
  const incomingBookingRef = useRef(null);
  const scheduledShownRef = useRef(new Set());
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isProfileDetailsVisible, setIsProfileDetailsVisible] = useState(false);
  const [weatherAlert, setWeatherAlert] = useState("Loading weather...");
  const [isUploadingPic, setIsUploadingPic] = useState(false);
  const [isEmergencyModalOpen, setIsEmergencyModalOpen] = useState(false);
  const [isSendingEmergency, setIsSendingEmergency] = useState(false);
  const [isSosExpanded, setIsSosExpanded] = useState(false);
  const [isUpdatingMobile, setIsUpdatingMobile] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);
  const [activeHire, setActiveHire] = useState(null);
  const [hireStage, setHireStage] = useState('accepted');
  const [pickupLocation, setPickupLocation] = useState(null);
  const [destinationLocation, setDestinationLocation] = useState(null);
  const locationInterval = useRef(null);

  const getBookings = async () => dispatch(fetchBookings());
  const getUsers = async () => dispatch(fetchUsers());
  const getVehicles = async () => dispatch(fetchVehicles());

  const getVehicleIcon = (vehicleType) => {
    const typeMap = {
      Car: carIcon, Van: vanIcon, Bus: busIcon, Lorry: lorryIcon,
      SUV: suvIcon, Cab: cabIcon, Forklift: forkliftIcon,
      "Cherry-Picker": cherryIcon, Crane: craneIcon, Trailer: lorryIcon
    };
    return typeMap[vehicleType] || carIcon;
  };

  const mapRole = (r) => {
    if (!r) return "D";
    const v = r.toString().trim().toUpperCase();
    if (v === "D" || v === "DRIVER") return "D";
    if (v === "S" || v === "SUPERVISOR") return "S";
    if (v === "E" || v === "EMPLOYEE") return "E";
    if (v === "A" || v === "ADMIN") return "A";
    return "D";
  };

  const mapStatus = (s) => {
    const v = s?.toString().trim().toUpperCase();
    return v === "ACTIVE" || v === "A" ? "A" : "I";
  };

  const normalizeVehicleType = (type) => {
    const t = (type ?? "").toString().trim();
    const typeCodeMap = {
      Car: "C", Van: "V", Lorry: "L", SUV: "S", Cab: "CA",
      Bus: "B", "Cherry Picker": "CH", Forklift: "F", Crane: "CR", Trailer: "T",
    };
    if (typeCodeMap[t]) return typeCodeMap[t];
    if (t.length > 0 && t.length <= 2) return t;
    return "C";
  };

  const normalizeBStatus = (value) => {
    const v = (value ?? "").toString().trim().toUpperCase();
    if (!v) return "";
    if (v.startsWith("P")) return "P";
    if (v.startsWith("B")) return "B";
    if (v.startsWith("C")) return "C";
    return v.length <= 2 ? v : v[0];
  };

  const getCurrentLocation = () => {
    return new Promise((resolve) => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const location = {
              latitude: pos.coords.latitude.toFixed(6),
              longitude: pos.coords.longitude.toFixed(6),
            };
            setHasLocationFix(true);
            resolve(location);
          },
          () => resolve(vehicleLocation),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
        );
      } else {
        resolve(vehicleLocation);
      }
    });
  };

  const updateVehicleInDB = async (newOStatus = null) => {
    if (!currentVehicle) return;
    const location = await getCurrentLocation();
    const vehicleTypeCode = normalizeVehicleType(currentVehicle.vehicleType || currentVehicle.vehicle_type);
    const statusCode = currentVehicle.status === "Active" ? "A" : currentVehicle.status === "Inactive" ? "I" : currentVehicle.status || "A";
    const oStatusValue = newOStatus || (isOnline ? "O" : "F");
    const bStatusValue = normalizeBStatus(currentVehicle.bStatus);
    
    const payload = [{
      vehicleId: currentVehicle.vehicleId,
      vehicleType: vehicleTypeCode,
      weightCat: currentVehicle.weightCat || "",
      driverSno: currentVehicle.driverSno || "",
      status: statusCode,
      bStatus: bStatusValue,
      oStatus: oStatusValue,
      latitude: oStatusValue === "F" ? "" : location.latitude,
      longitude: oStatusValue === "F" ? "" : location.longitude,
    }];

    try {
      await api.post("/VehicleRegister/PostVehicleDetails", payload);
      setVehicleLocation({ latitude: parseFloat(location.latitude), longitude: parseFloat(location.longitude) });
    } catch (err) {
      console.error("Failed to save location/status:", err);
    }
  };

  const updateVehicleBookingStatus = async (newBStatus = "B") => {
    if (!currentVehicle) return;
    const location = await getCurrentLocation();
    const vehicleTypeCode = normalizeVehicleType(currentVehicle.vehicleType || currentVehicle.vehicle_type);
    const statusCode = currentVehicle.status === "Active" ? "A" : currentVehicle.status === "Inactive" ? "I" : currentVehicle.status || "A";
    const bStatusValue = normalizeBStatus(newBStatus);
    const oStatusValue = isOnline ? "O" : "F";
    
    const payload = [{
      vehicleId: currentVehicle.vehicleId,
      vehicleType: vehicleTypeCode,
      weightCat: currentVehicle.weightCat || "",
      driverSno: currentVehicle.driverSno || "",
      status: statusCode,
      bStatus: bStatusValue,
      oStatus: oStatusValue,
      latitude: location.latitude,
      longitude: location.longitude,
    }];

    try {
      await api.post("/VehicleRegister/PostVehicleDetails", payload);
      setCurrentVehicle({ ...currentVehicle, bStatus: newBStatus });
    } catch (err) {
      console.error("Failed to update vehicle booking status:", err);
    }
  };

  const updateVehicleLocation = async () => {
    if (!currentVehicle) return;
    const location = await getCurrentLocation();
    const statusCode = currentVehicle.status === "Active" ? "A" : currentVehicle.status === "Inactive" ? "I" : currentVehicle.status || "A";
    const payload = [{ vehicleId: currentVehicle.vehicleId, status: statusCode, latitude: location.latitude, longitude: location.longitude }];

    try {
      await api.post("/VehicleRegister/UpdateVehicleLocation", payload);
      setVehicleLocation({ latitude: parseFloat(location.latitude), longitude: parseFloat(location.longitude) });
    } catch (err) {
      console.error("Failed to update vehicle location:", err);
    }
  };

  const isActiveBookingStatus = (value) => {
    const s = String(value || "").trim().toUpperCase();
    return s === "A" || s === "ACTIVE" || s === "EN" || s === "ENROUTE" || s === "IP" || s === "INPROGRESS";
  };

  const handleOpenEmergency = () => setIsEmergencyModalOpen(true);

  const handleSendEmergency = async ({ ecode, ediscription }) => {
    if (!driver.vehicleId) {
      toast.error("No vehicle assigned. Cannot send alert.");
      return;
    }

    setIsSendingEmergency(true);
    try {
      let activeBooking = null;
      if (hasActiveBooking) {
        try {
          const allBookings = await getBookings();
          const driverId = driver.driverSno || userDetails?.ServiceNo;
          const vehicleId = driver.vehicleId;
          const activeStatuses = ["A", "ACTIVE", "EN", "ENROUTE", "IP", "INPROGRESS"];
          const candidates = allBookings.filter((b) => {
            const status = (b.status || "").toString().trim().toUpperCase();
            return driverId && vehicleId && b.dSNo?.toString() === driverId.toString() && b.vId?.toString() === vehicleId.toString() && activeStatuses.includes(status);
          });
          if (candidates.length > 0) {
            activeBooking = [...candidates].sort((a, b) => new Date(b.bookingDT || b.bsTime || 0).getTime() - new Date(a.bookingDT || a.bsTime || 0).getTime())[0];
          }
        } catch (findErr) {
          console.error("Failed to find active booking for SOS:", findErr);
        }
      }

      await updateEmergency(driver.vehicleId, ecode, ediscription);
      chatWS.sendEmergency({
        vehicleId: driver.vehicleId, ecode, ediscription,
        driverName: driver.name || "Unknown Driver",
        driverSno: driver.driverSno, mobileNo: driver.mobileNo,
        ...(activeBooking ? { bId: activeBooking.bId, bookingSno: activeBooking.bookingSno || "", bookingStatus: activeBooking.status } : {}),
      });

      if (!hasActiveBooking && isOnline && currentVehicle) {
        await updateVehicleInDB("F");
        setIsOnline(false);
        setCurrentVehicle({ ...currentVehicle, oStatus: "F" });
        locationWS.disconnect();
      }

      toast.success("EMERGENCY ALERT SENT! Help is on the way.");
      setIsEmergencyModalOpen(false);
    } catch (error) {
      console.error("Emergency Alert Failed:", error);
      toast.error("Failed to send alert! Please call emergency services.");
    } finally {
      setIsSendingEmergency(false);
    }
  };

  const toggleOnlineStatus = async () => {
    if (!currentVehicle || isUpdatingStatus) return;
    const willBeOnline = !isOnline;
    setIsUpdatingStatus(true);

    try {
      if (isOnline && hasActiveBooking) {
        const driverId = String(userDetails?.ServiceNo || "").trim();
        const vehicleId = String(currentVehicle?.vehicleId || "").trim();
        const allBookings = await getBookings();
        const active = (allBookings || []).some((b) => String(b?.dSNo || "").trim() === driverId && String(b?.vId || "").trim() === vehicleId && isActiveBookingStatus(b?.status));

        if (active) {
          toast.error("Cannot go offline during active hire", { position: "top-center", autoClose: 3000 });
          return;
        }
        setHasActiveBooking(false);
        await updateVehicleBookingStatus("");
        setCurrentVehicle({ ...currentVehicle, bStatus: "" });
      }

      if (willBeOnline) {
        const vehicles = await getVehicles();
        const driverId = userDetails?.ServiceNo;
        const assigned = vehicles.find((v) => (v.vehicleId && currentVehicle.vehicleId && v.vehicleId.toString() === currentVehicle.vehicleId.toString()) || (driverId && v.driverSno && v.driverSno.toString() === driverId.toString()));
        const hasActiveSOS = !!(assigned?.ecode && assigned.ecode.toString().trim() !== "");
        if (hasActiveSOS) {
          toast.error("You cannot go ONLINE while an emergency alert is active. Please wait until admin resolves it.", { position: "top-center", autoClose: 4000 });
          return;
        }
      }

      await updateVehicleInDB(willBeOnline ? "O" : "F");
      setIsOnline(willBeOnline);
      setCurrentVehicle({ ...currentVehicle, oStatus: willBeOnline ? "O" : "F" });

      if (willBeOnline) {
        const locationData = {
          vehicleId: currentVehicle.vehicleId,
          driverSno: currentVehicle.driverSno || userDetails?.ServiceNo,
          driverName: driver.name,
          vehicle_type: currentVehicle.vehicleType,
          latitude: vehicleLocation.latitude,
          longitude: vehicleLocation.longitude,
          status: currentVehicle.status || "Active",
          bStatus: currentVehicle.bStatus || "",
          oStatus: "O",
        };
        locationWS.connect(authKey, locationData);
      } else {
        locationWS.disconnect();
      }

      toast.success(`You are now ${willBeOnline ? "ONLINE" : "OFFLINE"}!`, { position: "top-center", autoClose: 2500 });
    } catch (err) {
      toast.error("Failed to update status. Try again.");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const fetchDriverAndVehicle = async () => {
    try {
      const [users, vehicles] = await Promise.all([getUsers(), getVehicles()]);
      if (Array.isArray(users)) {
        const thisDriver = users.find((u) => u.ServiceNo === userDetails.ServiceNo);
        if (thisDriver) {
          setDriver({
            name: thisDriver.Name || "Driver",
            driverSno: thisDriver.ServiceNo,
            mobileNo: thisDriver.MobileNo || "N/A",
            profilePic: getProfilePictureUrl(thisDriver.ServiceNo),
            role: mapRole(thisDriver.role),
            status: mapStatus(thisDriver.status),
            password: thisDriver.password || "",
            file_path: thisDriver.file_path || null,
            vehicleId: "",
            vehicleType: "",
          });
        }
      }

      if (Array.isArray(vehicles)) {
        const assignedVehicle = vehicles.find((v) => v.driverSno === userDetails.ServiceNo);
        if (assignedVehicle) {
          setCurrentVehicle(assignedVehicle);
          const dbOStatus = (assignedVehicle.oStatus || "").trim();
          const online = dbOStatus === "O" || dbOStatus === "Online";
          setIsOnline(online);
          setDriver(prev => ({ ...prev, vehicleId: assignedVehicle.vehicleId, vehicleType: assignedVehicle.vehicleType || "Unknown" }));

          const hasValidCoords = assignedVehicle.latitude && assignedVehicle.longitude && !isNaN(parseFloat(assignedVehicle.latitude)) && !isNaN(parseFloat(assignedVehicle.longitude));
          if (hasValidCoords) {
            setVehicleLocation({ latitude: parseFloat(assignedVehicle.latitude), longitude: parseFloat(assignedVehicle.longitude) });
            setHasLocationFix(true);
          } else {
            getCurrentLocation().then((liveLocation) => {
              setVehicleLocation({ latitude: parseFloat(liveLocation.latitude), longitude: parseFloat(liveLocation.longitude) });
              setHasLocationFix(true);
            }).catch(() => {
              setVehicleLocation({ latitude: 6.9271, longitude: 79.8612 });
            });
          }
        }
      }
    } catch (err) {
      toast.error("Failed to load profile");
      console.error(err);
    }
  };

  const fetchWeatherAlert = async () => {
    try {
      const { latitude, longitude } = vehicleLocation;
      const weatherUrl = `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current_weather=true`;
      const res = await fetch(weatherUrl);
      const data = await res.json();
      const code = data.current_weather?.weathercode;
      const desc = {
        0: "Clear sky", 1: "Mainly clear", 2: "Partly cloudy", 3: "Overcast",
        45: "Fog", 51: "Light drizzle", 61: "Light rain", 63: "Rain", 65: "Heavy rain",
        71: "Light snow", 80: "Rain showers", 95: "Thunderstorm",
      }[code] || "Weather info unavailable";
      setWeatherAlert(desc);
    } catch {
      setWeatherAlert("Weather service unavailable");
    }
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image size should be less than 10MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImage(reader.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = async (croppedBlob) => {
    setShowCropper(false);
    setIsUploadingPic(true);
    try {
      const croppedFile = new File([croppedBlob], "profile.jpg", { type: "image/jpeg" });
      const formData = new FormData();
      formData.append("ServiceNo", driver.driverSno);
      formData.append("Name", driver.name);
      formData.append("MobileNo", driver.mobileNo);
      formData.append("role", driver.role);
      formData.append("status", driver.status);
      formData.append("password", driver.password || "");
      formData.append("ImageFile", croppedFile);
      if (driver.file_path) formData.append("file_path", driver.file_path);

      await api.post("/User/Fileupload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const timestamp = Date.now();
      const newPicUrl = `${getProfilePictureUrl(driver.driverSno)}&t=${timestamp}`;
      setDriver(prev => ({ ...prev, profilePic: newPicUrl }));
      toast.success("Profile picture updated successfully!");
    } catch (err) {
      console.error("Failed to upload profile picture:", err);
      toast.error("Failed to update profile picture");
    } finally {
      setIsUploadingPic(false);
      setSelectedImage(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCropperClose = () => {
    setShowCropper(false);
    setSelectedImage(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleMobileNumberUpdate = async (newMobileNumber) => {
    setIsUpdatingMobile(true);
    try {
      const formData = new FormData();
      formData.append("ServiceNo", driver.driverSno);
      formData.append("Name", driver.name);
      formData.append("MobileNo", newMobileNumber);
      formData.append("role", driver.role);
      formData.append("status", driver.status);
      formData.append("password", driver.password || "");
      if (driver.file_path) formData.append("file_path", driver.file_path);

      const response = await api.post("/User/Fileupload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (response?.data?.statusCode === 200) {
        setDriver(prev => ({ ...prev, mobileNo: newMobileNumber }));
        toast.success("Mobile number updated successfully!");
        return true;
      } else {
        throw new Error(response?.data?.message || "Update failed");
      }
    } catch (err) {
      console.error("Failed to update mobile number:", err);
      toast.error("Failed to update mobile number");
      throw err;
    } finally {
      setIsUpdatingMobile(false);
    }
  };

  const handlePasswordChange = async (currentPassword, newPassword) => {
    try {
      const formData = new FormData();
      formData.append("ServiceNo", driver.driverSno);
      formData.append("Name", driver.name);
      formData.append("MobileNo", driver.mobileNo);
      formData.append("role", driver.role);
      formData.append("status", driver.status);
      formData.append("password", newPassword);
      if (driver.file_path) formData.append("file_path", driver.file_path);

      const response = await api.post("/User/Fileupload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (response?.data?.statusCode === 200) {
        setDriver(prev => ({ ...prev, password: newPassword }));
        toast.success("Password changed successfully!");
        return true;
      } else {
        throw new Error(response?.data?.message || "Password change failed");
      }
    } catch (err) {
      console.error("Failed to change password:", err);
      throw err;
    }
  };

  const updateHireAssistance = (booking) => {
    if (!booking) return;
    const pickup = { lat: parseFloat(booking.slatitude), lng: parseFloat(booking.slongitude) };
    const destination = { lat: parseFloat(booking.elatitude), lng: parseFloat(booking.elongitude) };
    const distance = calculateDistance(pickup, destination);
    const eta = estimateETA(distance);
    const fare = calculateFare(distance);

    setActiveHire({
      hireId: booking.bId,
      bookingId: booking.bId,
      status: 'accepted',
      pickupLocation: pickup,
      destinationLocation: destination,
      distance: distance,
      eta: eta,
      fare: fare,
    });
    setHireStage('accepted');
    setPickupLocation(pickup);
    setDestinationLocation(destination);
  };

  const normalizeBookingStatusCode = (bookingOrValue) => {
    const raw = bookingOrValue?.BookingStatus ?? bookingOrValue;
    const v = String(raw || "").trim().toUpperCase();
    const hasScheduleTimes = Boolean(bookingOrValue?.bsTime && bookingOrValue?.beTime);
    const isScheduledLabel = v === "S" || v === "SHEDULE BOOKING" || v === "SCHEDULE BOOKING";
    const isRealtimeLabel = v === "R" || v === "REAL-TIME BOOKING" || v === "REAL TIME BOOKING";
    if (isScheduledLabel) return "S";
    if (hasScheduleTimes) return "S";
    return "R";
  };

  const normalizeStatus = (value) => String(value || "").trim().toUpperCase();
  const isPendingStatus = (value) => { const s = normalizeStatus(value); return s === "P" || s === "PENDING" || s === "R"; };
  const isScheduledBookingStatus = (value) => { const v = normalizeStatus(value); return v === "S" || v === "SHEDULE BOOKING" || v === "SCHEDULE BOOKING"; };
  const isScheduledCandidate = (booking) => {
    if (isScheduledBookingStatus(booking?.BookingStatus)) return true;
    const bookingDT = parseBookingDateTime(booking?.bookingDT);
    const bsTime = parseBookingDateTime(booking?.bsTime);
    if (!bookingDT || !bsTime) return false;
    return isPendingStatus(booking?.status) && bookingDT.getTime() !== bsTime.getTime();
  };

  const parseBookingDateTime = (dateStr) => {
    if (!dateStr) return null;
    const direct = new Date(dateStr);
    if (!isNaN(direct.getTime())) return direct;
    const parts = String(dateStr).trim().split(" ");
    if (parts.length >= 2 && parts[0].includes("/")) {
      const dateParts = parts[0].split("/");
      if (dateParts.length === 3) {
        const month = parseInt(dateParts[0], 10);
        const day = parseInt(dateParts[1], 10);
        const year = parseInt(dateParts[2], 10);
        const timePart = parts.slice(1).join(" ");
        const timeMatch = timePart.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?/i);
        if (timeMatch) {
          let hour = parseInt(timeMatch[1], 10);
          const minute = parseInt(timeMatch[2], 10);
          const second = parseInt(timeMatch[3] || "0", 10);
          const ampm = (timeMatch[4] || "").toUpperCase();
          if (ampm === "PM" && hour < 12) hour += 12;
          if (ampm === "AM" && hour === 12) hour = 0;
          return new Date(year, month - 1, day, hour, minute, second);
        }
      }
    }
    return null;
  };

  const handleAcceptBooking = async () => {
    if (!incomingBooking || isProcessing) return;
    setIsProcessing(true);

    try {
      updateHireAssistance(incomingBooking);
      const now = new Date();
      const isoDate = now.toISOString().slice(0, 10);
      const timeStr = now.toTimeString().slice(0, 8);
      const bsTimeFormatted = `${isoDate} ${timeStr}`;

      const payload = [{
        bId: parseInt(incomingBooking.bId),
        vId: incomingBooking.vId,
        dSNo: incomingBooking.dSNo,
        slatitude: parseFloat(incomingBooking.slatitude),
        slongitude: parseFloat(incomingBooking.slongitude),
        elatitude: parseFloat(incomingBooking.elatitude),
        elongitude: parseFloat(incomingBooking.elongitude),
        status: "A",
        bookingDT: incomingBooking.bookingDT,
        bsTime: bsTimeFormatted,
        beTime: incomingBooking.beTime || null,
        totalDestence: parseFloat(incomingBooking.totalDestence) || 0,
        dlatitude: parseFloat(incomingBooking.dlatitude || incomingBooking.slatitude),
        dlongitude: parseFloat(incomingBooking.dlongitude || incomingBooking.slongitude),
        bookingSno: incomingBooking.bookingSno || "",
        BookingStatus: normalizeBookingStatusCode(incomingBooking),
      }];

      await api.post("/BookingRegister/PostForkBooking", payload);
      bookingWS.sendStatusUpdate({
        bId: parseInt(incomingBooking.bId),
        status: "A",
        vId: incomingBooking.vId,
        dSNo: incomingBooking.dSNo,
        bookingSno: incomingBooking.bookingSno,
        dlatitude: parseFloat(incomingBooking.dlatitude || incomingBooking.slatitude),
        dlongitude: parseFloat(incomingBooking.dlongitude || incomingBooking.slongitude),
      });
      chatWS.sendStatus({
        bId: incomingBooking.bId,
        status: "A",
        message: "accepted hire look for my booking for more details",
        targetUserId: incomingBooking.bookingSno
      });

      await updateVehicleBookingStatus("B");
      locationWS.updateDriverData({
        vehicleId: incomingBooking.vId,
        vehicle_type: currentVehicle?.vehicleType || "Car",
        vehicleType: currentVehicle?.vehicleType || "Car",
        latitude: currentVehicle?.latitude || "",
        longitude: currentVehicle?.longitude || "",
        bStatus: "B",
        oStatus: "O",
        status: "A",
      });
      if (locationWS.isReady && locationWS.isReady()) locationWS.sendLocationUpdate();

      toast.success("Trip Accepted! Redirecting...", { position: "top-center" });
      navigate(`/driver-trip/${incomingBooking.bId}`);
    } catch (err) {
      console.error("Accept failed:", err);
      toast.error("Failed to accept trip");
      setIsProcessing(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!incomingBooking || isProcessing) return;
    setIsProcessing(true);

    try {
      const payload = [{
        bId: parseInt(incomingBooking.bId),
        vId: incomingBooking.vId,
        dSNo: incomingBooking.dSNo,
        slatitude: parseFloat(incomingBooking.slatitude),
        slongitude: parseFloat(incomingBooking.slongitude),
        elatitude: parseFloat(incomingBooking.elatitude),
        elongitude: parseFloat(incomingBooking.elongitude),
        status: "C",
        bookingDT: incomingBooking.bookingDT,
        bsTime: incomingBooking.bsTime || null,
        beTime: incomingBooking.beTime || null,
        totalDestence: parseFloat(incomingBooking.totalDestence) || 0,
        dlatitude: parseFloat(incomingBooking.dlatitude || incomingBooking.slatitude),
        dlongitude: parseFloat(incomingBooking.dlongitude || incomingBooking.slongitude),
        bookingSno: incomingBooking.bookingSno || "",
        BookingStatus: normalizeBookingStatusCode(incomingBooking),
      }];

      await api.post("/BookingRegister/PostForkBooking", payload);
      bookingWS.sendStatusUpdate({
        bId: parseInt(incomingBooking.bId),
        status: "C",
        vId: incomingBooking.vId,
        dSNo: incomingBooking.dSNo,
        bookingSno: incomingBooking.bookingSno,
        dlatitude: parseFloat(incomingBooking.dlatitude || incomingBooking.slatitude),
        dlongitude: parseFloat(incomingBooking.dlongitude || incomingBooking.slongitude),
      });
      chatWS.sendStatus({
        bId: incomingBooking.bId,
        status: "C",
        message: "canceled by driver check for another veicle",
        targetUserId: incomingBooking.bookingSno
      });

      await updateVehicleBookingStatus("");
      toast.info("Trip declined", { position: "top-center" });
      setIsOverlayVisible(false);
      setIncomingBooking(null);
    } catch (err) {
      console.error("Decline failed:", err);
      toast.error("Failed to decline");
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (!authKey) return;
    fetchDriverAndVehicle();
    fetchWeatherAlert();
  }, [authKey]);

  useEffect(() => {
    incomingBookingRef.current = incomingBooking;
  }, [incomingBooking]);

  useEffect(() => {
    if (!authKey || !currentVehicle?.vehicleId || !userDetails?.ServiceNo) {
      setHasActiveBooking(false);
      return;
    }

    let isMounted = true;
    const pollActiveBooking = async () => {
      try {
        const driverId = String(userDetails.ServiceNo || "").trim();
        const vehicleId = String(currentVehicle.vehicleId || "").trim();
        const allBookings = await getBookings();
        const active = (allBookings || []).some((b) => String(b?.dSNo || "").trim() === driverId && String(b?.vId || "").trim() === vehicleId && isActiveBookingStatus(b?.status));
        if (isMounted) setHasActiveBooking(active);
        if (!active && (currentVehicle?.bStatus === "B" || currentVehicle?.bStatus === "Booking")) {
          await updateVehicleBookingStatus("");
          if (isMounted) setCurrentVehicle({ ...currentVehicle, bStatus: "" });
        }
      } catch (err) {
        console.error("Failed to sync active booking status:", err);
      }
    };

    pollActiveBooking();
    const interval = setInterval(pollActiveBooking, 30000);
    return () => { isMounted = false; clearInterval(interval); };
  }, [authKey, currentVehicle?.vehicleId, currentVehicle?.bStatus, userDetails?.ServiceNo]);

  useEffect(() => {
    if (!isOnline || !authKey || !currentVehicle?.vehicleId || !userDetails?.ServiceNo) return;

    let isMounted = true;
    const windowMs = 2 * 60 * 1000;

    const pollScheduled = async () => {
      if (!isMounted || isOverlayVisible || isProcessing) return;
      try {
        const vehicleId = String(currentVehicle.vehicleId || "").trim();
        const driverId = String(userDetails.ServiceNo || "").trim();
        if (!vehicleId || !driverId) return;

        const bookings = await getBookingDetailsByVehicleId(vehicleId);
        const now = new Date();
        const due = (bookings || [])
          .filter((b) => String(b?.dSNo || "").trim() === driverId)
          .filter((b) => String(b?.vId || "").trim() === vehicleId)
          .filter((b) => isScheduledCandidate(b))
          .filter((b) => isPendingStatus(b?.status))
          .filter((b) => b?.bsTime);

        const sorted = due.sort((a, b) => (parseBookingDateTime(a.bsTime)?.getTime() || 0) - (parseBookingDateTime(b.bsTime)?.getTime() || 0));

        for (const booking of sorted) {
          const bId = booking?.bId;
          if (!bId) continue;
          const key = String(bId);
          if (scheduledShownRef.current.has(key)) continue;

          const startTime = parseBookingDateTime(booking.bsTime);
          if (!startTime) continue;
          const diff = now.getTime() - startTime.getTime();

          if (diff >= 0 && diff <= windowMs) {
            const scheduledBooking = { ...booking, BookingStatus: normalizeBookingStatusCode(booking) };
            setIncomingBooking(scheduledBooking);
            setIsOverlayVisible(true);
            scheduledShownRef.current.add(key);
            toast.info("Scheduled trip time reached", { position: "top-center", autoClose: 6000 });
            break;
          }
        }
      } catch (err) {
        console.error("Scheduled booking poll failed:", err);
      }
    };

    pollScheduled();
    const interval = setInterval(pollScheduled, 30000);
    return () => { isMounted = false; clearInterval(interval); };
  }, [isOnline, authKey, currentVehicle?.vehicleId, userDetails?.ServiceNo, isOverlayVisible, isProcessing]);

  useEffect(() => {
    if (!isOnline || !authKey || !currentVehicle || !userDetails?.ServiceNo) {
      signalrService.disconnect();
      bookingWS.disconnect();
      clearInterval(locationInterval.current);
      return;
    }

    signalrService.connect(authKey, userDetails.ServiceNo);
    bookingWS.connect(authKey, userDetails.ServiceNo, currentVehicle.vehicleId);
    chatWS.connect(authKey, userDetails.ServiceNo, "Driver");

    const processIncomingBooking = async (booking) => {
      const bIdIsValid = booking.bId && !String(booking.bId).startsWith("TEMP") && !isNaN(parseInt(booking.bId));
      if (bIdIsValid) {
        if (booking.dSNo && userDetails?.ServiceNo && booking.dSNo.toString() === userDetails.ServiceNo.toString()) {
          setIncomingBooking(booking);
          setIsOverlayVisible(true);
          toast.info(`New Trip Request #${booking.bId}`, { position: "top-center", autoClose: 6000 });
        }
      } else {
        try {
          let latestPendingBooking = null;
          if (booking?.vId) {
            const byVehicle = await getBookingDetailsByVehicleId(booking.vId);
            latestPendingBooking = (byVehicle || [])
              .filter(b => String(b?.dSNo || "").trim() === String(userDetails.ServiceNo || "").trim() && (String(b?.status || "").trim().toUpperCase() === "P" || String(b?.status || "").trim().toUpperCase() === "PENDING"))
              .sort((a, b) => new Date(b.bookingDT) - new Date(a.bookingDT))[0] || null;
          }

          if (!latestPendingBooking) {
            const allBookings = await getBookings();
            latestPendingBooking = (allBookings || [])
              .filter(b => String(b?.dSNo || "").trim() === String(userDetails.ServiceNo || "").trim() && (String(b?.status || "").trim().toUpperCase() === "P" || String(b?.status || "").trim().toUpperCase() === "PENDING"))
              .sort((a, b) => new Date(b.bookingDT) - new Date(a.bookingDT))[0] || null;
          }

          if (latestPendingBooking) {
            const actualBooking = { ...booking, ...latestPendingBooking };
            setIncomingBooking(actualBooking);
            setIsOverlayVisible(true);
            toast.info(`New Trip Request #${actualBooking.bId}`, { position: "top-center", autoClose: 6000 });
          }
        } catch (err) {
          setIncomingBooking(booking);
          setIsOverlayVisible(true);
          toast.info(`New Trip Request`, { position: "top-center", autoClose: 6000 });
        }
      }
    };

    signalrService.onNewBooking((booking) => {
      if (booking.type === "booking_cancelled" || booking.action === "booking_cancelled") {
        const currentBooking = incomingBookingRef.current;
        if (currentBooking && String(booking.bId) === String(currentBooking.bId)) {
          incomingBookingRef.current = null;
          setIncomingBooking(null);
          setIsOverlayVisible(false);
          toast.error("🚫 Booking cancelled by employee", { position: "top-center", autoClose: 4000 });
        }
        return;
      }
      processIncomingBooking(booking);
    });

    bookingWS.onNewBooking((booking) => {
      if (booking.type === "booking_cancelled" || booking.action === "booking_cancelled") {
        const currentBooking = incomingBookingRef.current;
        if (currentBooking && String(booking.bId) === String(currentBooking.bId)) {
          incomingBookingRef.current = null;
          setIncomingBooking(null);
          setIsOverlayVisible(false);
          toast.error("🚫 Booking cancelled by employee", { position: "top-center", autoClose: 4000 });
        }
        return;
      }
      processIncomingBooking(booking);
    });

    if (locationInterval.current) clearInterval(locationInterval.current);
    locationInterval.current = setInterval(() => updateVehicleLocation(), 30000);

    return () => {
      signalrService.disconnect();
      bookingWS.disconnect();
      if (locationInterval.current) clearInterval(locationInterval.current);
    };
  }, [isOnline, authKey, currentVehicle, userDetails?.ServiceNo]);

  useEffect(() => {
    const timer = setTimeout(fetchWeatherAlert, 5000);
    return () => clearTimeout(timer);
  }, [vehicleLocation]);

  useEffect(() => {
    if (isOnline && locationWS.isReady()) {
      locationWS.updateDriverData({
        latitude: vehicleLocation.latitude,
        longitude: vehicleLocation.longitude,
      });
    }
  }, [vehicleLocation, isOnline]);

  if (!authKey) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-8 text-center">
        <p className="text-sm text-gray-500">Please log in</p>
      </div>
    </div>;
  }

  return (
    <DriverLayout>
      {/* Header Section */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
              Driver Dashboard
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Welcome back, {driver.name.split(' ')[0]}
            </p>
          </div>
          <button
            onClick={toggleOnlineStatus}
            disabled={isUpdatingStatus || !driver.vehicleId}
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg transition ${
              !driver.vehicleId ? "bg-gray-200 text-gray-400 cursor-not-allowed" :
              isOnline ? "bg-red-600 text-white hover:bg-red-700" : "bg-emerald-600 text-white hover:bg-emerald-700"
            }`}
          >
            {isUpdatingStatus ? "Updating..." : (isOnline ? "Go Offline" : "Go Online")}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <StatCard title="Today Trips" value="0" icon={Navigation} color="blue" />
        <StatCard title="Total Trips" value="0" icon={Truck} color="green" />
        <StatCard title="Rating" value="4.8" icon={User} color="yellow" />
        <StatCard title="Earnings" value="LKR 0" icon={Clock} color="blue" />
      </div>

      {/* Profile and Vehicle Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Profile Card */}
        <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-shrink-0">
              <img
                src={driver.profilePic}
                alt="Profile"
                onClick={() => setIsProfileDetailsVisible(true)}
                className="w-14 h-14 rounded-full object-cover border-2 border-gray-200 cursor-pointer hover:border-gray-400 transition"
                onError={(e) => { e.target.src = defaultUserImg; }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingPic}
                className="absolute bottom-0 right-0 bg-gray-900 hover:bg-gray-800 text-white rounded-full p-1 shadow-lg transition"
              >
                {isUploadingPic ? (
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleProfilePictureChange} className="hidden" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 truncate">{driver.name}</h2>
              <p className="text-xs text-gray-500">ID: {driver.driverSno}</p>
              <p className="text-xs text-gray-500">Mobile: {driver.mobileNo}</p>
            </div>
          </div>
          {!driver.vehicleId && (
            <p className="mt-3 text-xs text-red-600 bg-red-50 px-2 py-1 rounded-lg">
              ⚠️ Vehicle Not Assigned Yet
            </p>
          )}
          {isOnline && hasActiveBooking && (
            <p className="mt-3 text-xs text-red-600 bg-red-50 px-2 py-1 rounded-lg">
              ⚠️ Cannot go offline during active hire
            </p>
          )}
        </div>

        {/* Vehicle Info Card */}
        <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Assigned Vehicle</h3>
          {currentVehicle?.vehicleId ? (
            <VehicleInfoCard vehicle={currentVehicle} />
          ) : (
            <p className="text-sm text-gray-500">No vehicle assigned</p>
          )}
        </div>

        {/* Weather Card */}
        <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Weather</h3>
          <WeatherCard weather={weatherAlert} />
        </div>
      </div>

      {/* Map Section */}
      <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden mb-4">
        <div className="p-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-gray-600" />
            <h2 className="text-sm font-semibold text-gray-900">Live Vehicle Location</h2>
            <StatusBadge isOnline={isOnline} />
          </div>
        </div>
        <div className="h-[400px] w-full">
          <MapContainer
            center={[vehicleLocation.latitude, vehicleLocation.longitude]}
            zoom={16}
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <RecenterButton position={[vehicleLocation.latitude, vehicleLocation.longitude]} zoom={16} />
            <AnimatedMapCenter position={[vehicleLocation.latitude, vehicleLocation.longitude]} />
            {hasLocationFix && (
              <CircleMarker
                center={[vehicleLocation.latitude, vehicleLocation.longitude]}
                radius={12}
                fillColor="#3B82F6"
                color="#1E40AF"
                weight={3}
                opacity={1}
                fillOpacity={0.8}
              >
                <Popup>
                  <div className="text-center">
                    <p className="font-semibold text-sm">{driver.vehicleId}</p>
                    <p className="text-xs text-gray-600">{driver.vehicleType}</p>
                    <p className="text-xs text-gray-500 mt-1">Driver: {driver.name}</p>
                  </div>
                </Popup>
              </CircleMarker>
            )}
          </MapContainer>
        </div>
      </div>

      {/* Active Bookings Panel */}
      <ActiveBookingsPanel driverSno={driver.driverSno} isOnline={isOnline} />

      {/* Modals */}
      <BookingAlertOverlay
        isProcessing={isProcessing}
        booking={incomingBooking}
        isVisible={isOverlayVisible}
        onAccept={handleAcceptBooking}
        onCancel={handleCancelBooking}
      />

      <UserDetailsOverlay
        isVisible={isProfileDetailsVisible}
        onClose={() => setIsProfileDetailsVisible(false)}
        userDetails={driver}
        userRole="Driver"
        fileInputRef={fileInputRef}
        onProfilePictureChange={handleProfilePictureChange}
        isUploadingPic={isUploadingPic}
        onMobileNumberUpdate={handleMobileNumberUpdate}
        isUpdatingMobile={isUpdatingMobile}
        canEditMobile={true}
        onPasswordChange={handlePasswordChange}
        isUpdatingPassword={isUpdatingPassword}
        canChangePassword={true}
      />

      <ProfilePictureCropper
        isOpen={showCropper}
        onClose={handleCropperClose}
        onCropComplete={handleCropComplete}
        imageSrc={selectedImage}
      />

      <EmergencyAlertModal
        isOpen={isEmergencyModalOpen}
        onClose={() => setIsEmergencyModalOpen(false)}
        onSend={handleSendEmergency}
        isSending={isSendingEmergency}
      />

      {/* Emergency SOS Button - Update z-index */}
<div className="fixed right-0 top-1/2 -translate-y-1/2" style={{ zIndex: 9997 }}>
  <AnimatePresence>
    {!isSosExpanded ? (
      <motion.button
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        onClick={() => setIsSosExpanded(true)}
        className="bg-red-50 hover:bg-red-100 border-l-2 border-red-600 shadow-lg py-3 px-1 rounded-l-lg flex items-center justify-center transition-colors"
        title="SOS"
      >
        <ChevronLeft className="w-4 h-4 text-red-600" />
      </motion.button>
    ) : (
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="bg-white border-l-2 border-red-600 shadow-lg rounded-l-lg p-3 flex items-center gap-2"
      >
        <button onClick={() => setIsSosExpanded(false)} className="p-1 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-600 transition-colors">
          <ChevronRight className="w-4 h-4" />
        </button>
        <div className="flex flex-col items-center">
          <span className="text-[10px] font-bold text-red-800 uppercase">Emergency</span>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleOpenEmergency}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-red-700 text-white shadow-lg border-2 border-red-100"
          >
            <AlertTriangle className="w-5 h-5" />
          </motion.button>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
</div>

      {/* Hire Assistance Bubble */}
      {activeHire && (
        <HireAssistanceBubble
          hireData={activeHire}
          driverInfo={{
            name: driver.name,
            phone: driver.mobileNo,
            rating: 4.8,
            vehicleInfo: `${driver.vehicleType} • ${driver.vehicleId}`,
            profileImage: driver.profilePic,
          }}
          driverLocation={{ lat: vehicleLocation.latitude, lng: vehicleLocation.longitude }}
          onCallDriver={() => { if (driver.mobileNo) window.location.href = `tel:${driver.mobileNo}`; }}
          onMessageDriver={() => toast.info("Messaging feature coming soon!")}
          onEmergency={handleOpenEmergency}
          isLocationSharing={isOnline}
        />
      )}
    </DriverLayout>
  );
}